import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
  PianoGameSession,
  type PianoSessionStatus,
} from '../../game/piano/pianoSession';
import {
  buildTutorialPianoChart,
  getPianoTutorialOverlays,
  type PianoTutorialOverlay,
} from '../../game/piano/tutorialPianoChart';
import { markTutorialSeen } from '../../game/tutorialFlag';
import { saveModePref } from '../../game/modePref';
import { modePath } from '../../game/modeRouting';
import { loadKeybindPrefs, maniaLaneCodes } from '../../game/keybindPrefs';
import '../play.css';
import './pianoTutorial.css';

const TUTORIAL_CHART = buildTutorialPianoChart();

function pickOverlay(
  overlays: PianoTutorialOverlay[],
  beat: number,
): PianoTutorialOverlay {
  for (let i = overlays.length - 1; i >= 0; i -= 1) {
    const o = overlays[i];
    if (beat >= o.fromBeat && beat < o.untilBeat) {
      return o;
    }
  }
  return overlays[0];
}

/**
 * Quick onboarding for Piano mode. Runs a handcrafted teaching chart
 * through {@link PianoGameSession} so a brand-new player learns the
 * "tap-when-it-meets-the-line" rhythm in a controlled environment
 * before being dropped into a real song.
 *
 * Mirrors the Pulse tutorial's beat-synced overlay pattern (each
 * lesson chunk advances when the player's beat crosses a threshold)
 * rather than Circles' static rules card, because Piano has three
 * distinct tile shapes (tap, hold, dual) that benefit from being
 * introduced as the player encounters them.
 */
export function PianoTutorialPage() {
  const navigate = useNavigate();
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const sessionRef = useRef<PianoGameSession | null>(null);
  const [needsTap, setNeedsTap] = useState(true);
  const [status, setStatus] = useState<PianoSessionStatus>('idle');
  const isTouch = useMemo(
    () =>
      typeof window !== 'undefined' &&
      window.matchMedia('(pointer: coarse)').matches,
    [],
  );
  const overlays = useMemo(
    () => getPianoTutorialOverlays(isTouch),
    [isTouch],
  );
  const [overlay, setOverlay] = useState<PianoTutorialOverlay>(overlays[0]);

  useEffect(() => {
    saveModePref('piano');
  }, []);

  useEffect(() => {
    return () => {
      if (sessionRef.current) {
        void sessionRef.current.destroy();
        sessionRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    if (status === 'idle') {
      // No session running yet — skip the RAF loop entirely. The
      // overlay is stable until the player taps Start, and pumping
      // pickOverlay every frame against a null session is just
      // wasted wakeups for the tab.
      return undefined;
    }
    let raf = 0;
    const loop = () => {
      const session = sessionRef.current;
      if (session) {
        const beat = session.getCurrentBeat();
        if (Number.isFinite(beat)) {
          const next = pickOverlay(overlays, beat);
          setOverlay((cur) => (cur.fromBeat === next.fromBeat ? cur : next));
        }
      }
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(raf);
  }, [overlays, status]);

  const startTutorial = useCallback(async () => {
    if (!canvasRef.current) {
      return;
    }
    setNeedsTap(false);
    if (sessionRef.current) {
      await sessionRef.current.destroy();
      sessionRef.current = null;
    }
    const session = new PianoGameSession({
      chart: TUTORIAL_CHART,
      canvas: canvasRef.current,
      audioOffsetMs: 0,
      onStatusChange: (s) => setStatus(s),
      onComplete: () => {
        // Any tutorial play-through — pass or fail — counts as
        // "seen" so the home-page gate doesn't trap the player in a
        // loop: the failure overlay routes them to /piano via
        // `Browse tracks`, and if the gate flag were still unset the
        // home page would immediately bounce them back here.
        // Players who want to revisit the tutorial can still use the
        // "How to play" link in the home-page CTA.
        markTutorialSeen('piano');
      },
      // Tutorial doesn't bank stars/cups — `enableLoops` stays false
      // so the run finishes naturally on completion instead of looping
      // into infinite-mode repeats.
      //
      // Stay forgiving: real Piano play is instadeath-on-miss, but a
      // beginner must be able to fumble a tile and still reach the
      // hold + chord sections, so the tutorial keeps the HP-drain model.
      instadeathOnMiss: false,
    });
    sessionRef.current = session;
    try {
      await session.start();
    } catch (err) {
      console.error('Failed to start Piano tutorial:', err);
      setNeedsTap(true);
    }
  }, []);

  useEffect(() => {
    if (!needsTap) {
      return;
    }
    const laneCodes = maniaLaneCodes(loadKeybindPrefs(), 'piano');
    const onKey = (e: KeyboardEvent) => {
      // Any of the player's currently-bound lane keys (or
      // Space / Enter) starts the run. Reading from prefs keeps
      // the tutorial honest after a rebind.
      if (
        e.key === ' ' ||
        e.code === 'Space' ||
        e.key === 'Enter' ||
        laneCodes.has(e.code)
      ) {
        e.preventDefault();
        void startTutorial();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [needsTap, startTutorial]);

  const skipTutorial = () => {
    markTutorialSeen('piano');
    navigate(modePath('piano'));
  };

  return (
    <div className="play-page tutorial-page" data-testid="piano-tutorial-page">
      <div className="play-canvas-wrap">
        <canvas
          ref={canvasRef}
          className="play-canvas"
          data-testid="piano-tutorial-canvas"
          onClick={() => {
            if (needsTap) {
              void startTutorial();
            }
          }}
        />

        {needsTap && (
          <div className="play-overlay" data-testid="piano-tutorial-start-overlay">
            <div className="play-overlay-card">
              <h2>Welcome to Piano</h2>
              <PianoTutorialDemo />
              <p>
                {isTouch ? 'Tap each lane' : (
                  <>
                    Press <kbd>Q</kbd> <kbd>W</kbd> <kbd>E</kbd> <kbd>R</kbd>
                    {' '}(or <kbd>D</kbd> <kbd>F</kbd> <kbd>J</kbd> <kbd>K</kbd>)
                  </>
                )}{' '}
                as the tiles meet the line. Hold tiles have arrows — keep your
                finger down until the top of the ribbon reaches the bar.
              </p>
              <p className="muted faint">
                30-second tutorial &middot; headphones recommended.
              </p>
              <div className="play-overlay-actions">
                <button
                  type="button"
                  className="btn btn-primary btn-large"
                  onClick={() => void startTutorial()}
                  data-testid="piano-tutorial-start"
                >
                  Start tutorial
                </button>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={skipTutorial}
                  data-testid="piano-tutorial-skip"
                >
                  Skip &mdash; take me to the songs
                </button>
              </div>
            </div>
          </div>
        )}

        {(status === 'completed' || status === 'failed') && (
          <div
            className="play-overlay"
            data-testid="piano-tutorial-complete-overlay"
          >
            <div className="play-overlay-card">
              <h2>{status === 'completed' ? "You're ready" : 'Try again?'}</h2>
              <p>
                {status === 'completed'
                  ? 'Pick a real track to start collecting stars.'
                  : 'Take another shot — the real songs ramp up gradually from On & On.'}
              </p>
              <div className="play-overlay-actions">
                <Link
                  to={modePath('piano')}
                  className="btn btn-primary btn-large"
                >
                  Browse tracks
                </Link>
                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => {
                    setNeedsTap(true);
                    setStatus('idle');
                  }}
                >
                  Replay tutorial
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/*
       * Sibling of the canvas wrap (not a child) so on narrow viewports the
       * overlay flows below the playfield instead of covering it. On wider
       * viewports CSS lifts it back into a floating overlay anchored to the
       * canvas — see .tutorial-overlay in play.css.
       *
       * Hide once the run finishes: the completion overlay (.play-overlay)
       * lives inside .play-canvas-wrap and only covers the canvas, so on
       * mobile (where this overlay is a sibling sitting *below* the canvas)
       * leaving it visible would show stale instructions next to the
       * "You're ready" card.
       */}
      {!needsTap && status !== 'completed' && status !== 'failed' && (
        <div
          className="tutorial-overlay"
          data-testid="piano-tutorial-overlay-text"
        >
          <div className="tutorial-overlay-card">
            <h3>{overlay.title}</h3>
            <p>{overlay.body}</p>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Looped 3-second SVG demo shown above the "Start tutorial" button —
 * a 4-lane piano playfield with a tap tile cruising down lane 2, a
 * pulsing hit-line at the bottom, and a "TAP!" / "HIT!" callout on
 * arrival. Mirrors the live playfield (vertical lanes, tiles
 * descending toward a horizontal bar) so the demo sets the same
 * mental model the player will see when the tutorial starts. All
 * animation lives in pianoTutorial.css keyframes; the SVG geometry
 * here is intentionally simple so the timing is easy to read and
 * tune.
 */
function PianoTutorialDemo() {
  return (
    <svg
      viewBox="0 0 320 130"
      className="piano-tutorial-demo"
      role="img"
      aria-label="Demo: tap each falling tile when it reaches the hit line"
      data-testid="piano-tutorial-demo"
    >
      <defs>
        <linearGradient id="pt-bg" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a0e25" />
          <stop offset="1" stopColor="#14193a" />
        </linearGradient>
        <linearGradient id="pt-tile" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(22, 224, 255, 0.85)" />
          <stop offset="1" stopColor="#16e0ff" />
        </linearGradient>
        <linearGradient id="pt-hold" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="rgba(59, 255, 157, 0.85)" />
          <stop offset="1" stopColor="#3bff9d" />
        </linearGradient>
      </defs>
      <rect width="320" height="130" fill="url(#pt-bg)" />
      <g className="piano-tutorial-demo-lanes">
        {[0, 1, 2, 3].map((laneIdx) => (
          <rect
            key={laneIdx}
            x={36 + laneIdx * 62}
            y={4}
            width={58}
            height={112}
            rx={4}
            className="piano-tutorial-demo-lane"
          />
        ))}
      </g>
      <line
        className="piano-tutorial-demo-hitline"
        x1="32"
        y1="106"
        x2="288"
        y2="106"
      />
      {/* Falling tap tile in lane 2 (zero-indexed lane 1 = W). */}
      <g className="piano-tutorial-demo-tap">
        <rect x="98" y="0" width="58" height="18" rx="4" fill="url(#pt-tile)" />
      </g>
      {/* Falling hold ribbon in lane 3 (E) with three upward chevrons. */}
      <g className="piano-tutorial-demo-hold">
        <rect x="160" y="0" width="58" height="48" rx="4" fill="url(#pt-hold)" />
        <g className="piano-tutorial-demo-chevrons" stroke="#ffffff" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" opacity="0.7">
          <polyline points="174,42 189,32 204,42" />
          <polyline points="174,28 189,18 204,28" />
        </g>
      </g>
      {/* Key labels along the bottom. */}
      {['Q', 'W', 'E', 'R'].map((label, laneIdx) => (
        <text
          key={label}
          className="piano-tutorial-demo-key"
          x={36 + laneIdx * 62 + 29}
          y="125"
          textAnchor="middle"
        >
          {label}
        </text>
      ))}
      <text
        className="piano-tutorial-demo-hit"
        x="127"
        y="98"
        textAnchor="middle"
      >
        TAP!
      </text>
    </svg>
  );
}
